import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { buildUrl, BACKEND_CONFIG } from '../config/backend';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Verificar se o token está expirado
  const isTokenExpired = useCallback(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return true;

    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Erro ao verificar expiração do token:', error);
      return true;
    }
  }, []);

  // Refresh token
  const refreshToken = useCallback(async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      
      if (!refreshTokenValue) {
        throw new Error('Refresh token não encontrado');
      }

      const response = await fetch(buildUrl(BACKEND_CONFIG.ENDPOINTS.AUTH.REFRESH), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshTokenValue })
      });

      const data = await response.json();

      if (data.success) {
        // Atualizar tokens
        localStorage.setItem('accessToken', data.data.accessToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        
        return { success: true, data: data.data };
      } else {
        throw new Error(data.message || 'Erro ao renovar token');
      }
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      // NÃO chamar logout aqui, apenas retornar erro
      return { success: false, message: error.message };
    }
  }, []); // NUNCA incluir logout nas dependências!

  // Logout
  const logout = useCallback(async (showToast = true) => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      
      if (refreshTokenValue) {
        // Tentar fazer logout no backend
        try {
          await fetch(buildUrl(BACKEND_CONFIG.ENDPOINTS.AUTH.LOGOUT), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken: refreshTokenValue })
          });
        } catch (error) {
          console.error('Erro ao fazer logout no backend:', error);
          // Continua com o logout local mesmo se o backend falhar
        }
      }
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      // Limpar dados locais
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('loginTimestamp');
      
      console.log('🧹 Dados limpos do localStorage');
      
      setUser(null);
      setIsAuthenticated(false);
      
      if (showToast) {
        toast.info('Logout realizado com sucesso');
      }
    }
  }, []);

  // Verificar status da autenticação
  const checkAuthStatus = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const refreshTokenValue = localStorage.getItem('refreshToken');
      const userData = localStorage.getItem('user');

      console.log('🔍 checkAuthStatus:', { 
        accessToken: !!accessToken, 
        refreshToken: !!refreshTokenValue,
        userData: !!userData 
      });

      if (accessToken && userData) {
        const parsedUser = JSON.parse(userData);
        console.log('✅ Usuário encontrado no localStorage:', parsedUser);
        
        // Verificar se o token está expirado
        if (isTokenExpired()) {
          console.log('⚠️ Token expirado, tentando renovar...');
          
          if (refreshTokenValue) {
            const refreshResult = await refreshToken();
            if (refreshResult.success) {
              console.log('✅ Token renovado com sucesso');
              setUser(parsedUser);
              setIsAuthenticated(true);
            } else {
              console.log('❌ Falha ao renovar token, fazendo logout');
              await logout();
            }
          } else {
            console.log('❌ Sem refresh token, fazendo logout');
            await logout();
          }
        } else {
          console.log('✅ Token válido, usuário autenticado');
          setUser(parsedUser);
          setIsAuthenticated(true);
        }
      } else {
        console.log('❌ Nenhum usuário encontrado no localStorage');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      await logout();
    } finally {
      setLoading(false);
    }
  }, [isTokenExpired, refreshToken, logout]);

  // Login
  const login = useCallback(async (username, password) => {
    try {
      console.log('🚀 Tentando fazer login para:', username);
      
      const response = await fetch(buildUrl(BACKEND_CONFIG.ENDPOINTS.AUTH.LOGIN), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();
      console.log('📡 Resposta do login:', data);

      if (data.success) {
        // Salvar tokens e dados do usuário
        localStorage.setItem('accessToken', data.data.accessToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        localStorage.setItem('user', JSON.stringify(data.data.user));
        
        // Salvar timestamp do login para referência
        localStorage.setItem('loginTimestamp', Date.now().toString());
        
        console.log('💾 Dados salvos no localStorage');
        console.log('👤 Usuário:', data.data.user);
        console.log('🕐 Timestamp do login:', new Date().toISOString());
        
        // Atualizar estado de forma síncrona
        setUser(data.data.user);
        setIsAuthenticated(true);
        
        // Forçar re-renderização
        setTimeout(() => {
          setUser(prev => ({ ...prev, _timestamp: Date.now() }));
        }, 100);
        
        toast.success('Login realizado com sucesso!');
        return { success: true, data: data.data };
      } else {
        toast.error(data.message || 'Erro no login');
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Erro no login:', error);
      toast.error('Erro de conexão. Verifique se o backend está rodando.');
      return { success: false, message: 'Erro de conexão' };
    }
  }, []);

  // Obter token de acesso válido
  const getValidToken = useCallback(async () => {
    if (isTokenExpired()) {
      const refreshResult = await refreshToken();
      if (!refreshResult.success) {
        return null;
      }
    }
    
    return localStorage.getItem('accessToken');
  }, [isTokenExpired, refreshToken]);

  // Obter informações da sessão atual
  const getSessionInfo = useCallback(() => {
    const loginTimestamp = localStorage.getItem('loginTimestamp');
    const accessToken = localStorage.getItem('accessToken');
    
    let sessionInfo = {
      isLoggedIn: isAuthenticated,
      user: user,
      loginTime: loginTimestamp ? new Date(parseInt(loginTimestamp)) : null,
      tokenExpired: isTokenExpired(),
      hasValidToken: !!accessToken && !isTokenExpired()
    };

    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        sessionInfo.tokenExpiry = new Date(payload.exp * 1000);
        sessionInfo.tokenIssuedAt = new Date(payload.iat * 1000);
      } catch (error) {
        console.error('Erro ao decodificar token:', error);
      }
    }

    return sessionInfo;
  }, [isAuthenticated, user, isTokenExpired]);

  // Verificar se o usuário está autenticado ao carregar
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Verificação automática de expiração do token
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenExpiry = async () => {
      if (isTokenExpired()) {
        console.log('🕐 Token expirado detectado automaticamente');
        const refreshResult = await refreshToken();
        if (!refreshResult.success) {
          console.log('❌ Falha ao renovar token automaticamente, fazendo logout');
          await logout(false); // Sem toast para logout automático
        }
      }
    };

    // Verificar a cada 5 minutos
    const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, isTokenExpired, refreshToken, logout]);

  // Log do estado atual sempre que mudar
  useEffect(() => {
    console.log('🔄 Estado do useAuth atualizado:', { user, isAuthenticated, loading });
    
    if (isAuthenticated) {
      const sessionInfo = getSessionInfo();
      console.log('📊 Informações da sessão:', sessionInfo);
    }
  }, [user, isAuthenticated, loading, getSessionInfo]);

  return {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    refreshToken,
    getValidToken,
    checkAuthStatus,
    getSessionInfo
  };
};
